import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Heart, Sparkles, Users, LogOut } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { CHARACTERS } from '../lib/levels';

const REACTIONS = ['❤️', '🎉', '🌟', '💪'];

export default function ExtendedDashboard() {
  const { user, logout } = useAuth();
  const [feed, setFeed] = useState({ children: [], milestones: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/crew/feed');
      setFeed(data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const react = async (milestoneId, emoji) => {
    setBusy(milestoneId + emoji);
    // Optimistic update
    setFeed((prev) => ({ ...prev, milestones: prev.milestones.map((m) => m.id === milestoneId ? { ...m, my_reaction: emoji } : m) }));
    try {
      await api.post(`/crew/react/${milestoneId}`, { emoji });
      toast.success('Cheer sent!');
    } catch (e) {
      toast.error('Failed');
      load();
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-navy text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-gold flex items-center justify-center text-navy font-heading text-xl font-bold">O</div>
            <div>
              <div className="font-heading text-lg font-bold">OuiStuPaStu</div>
              <div className="text-gold text-[11px] font-heading font-bold uppercase tracking-wider">Support Crew</div>
            </div>
          </div>
          <button onClick={logout} className="text-white/70 hover:text-white inline-flex items-center gap-1 text-sm" data-testid="crew-logout"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="extended-dashboard">
        <section className="rounded-3xl bg-gradient-to-br from-navy via-teal to-navy text-white p-5 shadow-squishyLg">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><Heart className="w-7 h-7 text-gold" fill="currentColor" /></div>
            <div>
              <div className="text-gold text-[10px] font-heading font-bold uppercase tracking-wider">Welcome, {user?.name?.split(' ')[0]}</div>
              <h1 className="font-heading text-2xl font-bold leading-tight">Cheer them on.</h1>
              <p className="text-white/85 text-sm mt-1">You'll see every milestone your special little person hits. Send a cheer whenever you feel proud.</p>
            </div>
          </div>
        </section>

        {feed.children.length > 0 && (
          <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-teal" />
              <h2 className="font-heading text-lg font-bold text-navy">Who you cheer on</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {feed.children.map((c) => {
                const ch = CHARACTERS[c.character] || CHARACTERS.stupidopoulou;
                return (
                  <div key={c.id} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-cream border-2 border-cream" data-testid={`crew-child-${c.id}`}>
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white">
                      <img src={ch.image} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="font-heading font-bold text-navy text-sm">{c.name}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="space-y-3" data-testid="crew-feed">
          <div className="flex items-center gap-2 px-1">
            <Sparkles className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-lg font-bold text-navy">Recent milestones</h2>
          </div>
          {loading ? (
            <div className="p-6 text-center text-navy/60 font-heading">Loading…</div>
          ) : feed.milestones.length === 0 ? (
            <div className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream text-center">
              <div className="text-4xl mb-2">🌱</div>
              <p className="text-navy font-heading font-bold">No milestones yet</p>
              <p className="text-navy/60 text-sm mt-1">You'll see achievements here as they happen.</p>
            </div>
          ) : (
            feed.milestones.map((m) => (
              <div key={m.id} className="bg-white rounded-3xl p-4 shadow-squishy border-2 border-cream" data-testid={`milestone-${m.id}`}>
                <div className="flex items-start gap-3">
                  <div className="text-4xl shrink-0">{m.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-navy text-sm">{m.child_name} just earned <span className="text-teal">{m.label}</span></div>
                    <div className="text-navy/60 text-xs mt-0.5">{m.desc}</div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {REACTIONS.map((emoji) => {
                        const active = m.my_reaction === emoji;
                        return (
                          <button key={emoji} onClick={() => react(m.id, emoji)} disabled={busy === m.id + emoji}
                            className={`px-3 py-1.5 rounded-full text-xl transition-transform hover:scale-110 active:scale-95 border-2 ${active ? 'bg-gold border-gold shadow-gold' : 'bg-white border-slate-200 hover:border-teal'}`}
                            data-testid={`react-${m.id}-${emoji}`}>
                            {emoji}
                          </button>
                        );
                      })}
                      {m.my_reaction && <span className="text-xs text-teal font-heading font-bold">✓ Cheer sent</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </section>
      </main>
    </div>
  );
}
